import React from 'react';
import { LoadScript, GoogleMap, Marker, InfoWindow, Polygon } from '@react-google-maps/api';
import useGetLocations from './hooks/useGetLocations';
import { Box, Button, Typography, FormControl, Select, MenuItem } from '@mui/material';
import { useHistory } from 'react-router-dom';

const containerStyle = {
  width: '100%',
  height: '70vh',
};

const irelandCenter = { lat: 53.1424, lng: -7.6921 };
const mapZoom = 7;

const driverIconSvg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="18" fill="#2563eb" stroke="white" stroke-width="3"/>
    <text x="20" y="28" text-anchor="middle" fill="white" font-size="16">🚛</text>
  </svg>
`;

const pinIconSvg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <path d="M20 2C12 2 6 8 6 16c0 8 14 22 14 22s14-14 14-22c0-8-6-14-14-14z" fill="#2563eb"/>
    <circle cx="20" cy="16" r="6" fill="white"/>
  </svg>
`;

const destinationIconSvg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <path d="M20 2C12 2 6 8 6 16c0 8 14 22 14 22s14-14 14-22c0-8-6-14-14-14z" fill="#94d82d"/>
    <circle cx="20" cy="16" r="6" fill="white"/>
  </svg>
`;

const MapView = () => {
  const {
    drivers,
    fetchAgain,
    refreshing,
    selectedDriver,
    setSelectedDriver,
    pinData,
    selectedPin,
    setSelectedPin,
    polyLine,
    selectedArea,
    setSelectedArea,
    allDestinations,
    hoverDestination,
    setHoverDestination,
    selectedData,
    setSelectedData,
  } = useGetLocations();

  const history = useHistory();

  // ✅ group polygons
  const grouped = polyLine.reduce((groups, p) => {
    if (!groups[p.mapLabel]) groups[p.mapLabel] = { path: [], info: p };
    groups[p.mapLabel].path.push({ lat: p.latitude, lng: p.longitude, sortOrder: p.sortOrder });
    return groups;
  }, {});
  Object.values(grouped).forEach(g => {
    g.path.sort((a, b) => a.sortOrder - b.sortOrder);
  });

  // ✅ unique pins
  const uniquePins = Object.values(
    pinData?.reduce((acc, pin) => {
      if (!acc[pin.siteAssessmentID]) {
        acc[pin.siteAssessmentID] = pin;
      }
      return acc;
    }, {}),
  );

  // ✅ helpers to safely create sizes
  const getScaledSize = (w, h) => (window.google ? new window.google.maps.Size(w, h) : undefined);

  const getAnchor = (x, y) => (window.google ? new window.google.maps.Point(x, y) : undefined);

  // helper function
  const isValidLatLng = (lat, lng) => {
    return typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng);
  };

  const showDataDropdown = ['Show All Data', 'Show drivers only', 'Show active sites only', 'Show destinations only'];

  return (
    <div style={{ width: '100%', height: 'calc(100vh - 150px)', position: 'relative' }}>
      <div>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Manage Role</h2>
          <Box display="flex" alignItems="center" gap={3}>
            <Button variant="contained" color="primary" onClick={() => history.push('/')}>
              Home
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 2 }}>
          <Typography variant="h3" sx={{ fontSize: '18px', fontWeight: 'bold' }}>
            Active Drivers: {drivers.length}
          </Typography>

          <Button
            variant="contained"
            onClick={fetchAgain}
            disabled={refreshing}
            style={{ minWidth: '140px', height: '40px' }}>
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>

          <FormControl sx={{ minWidth: 180 }}>
            <Select value={selectedData} onChange={e => setSelectedData(e.target.value)} size="small">
              {showDataDropdown.map((option, idx) => (
                <MenuItem key={idx} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </div>

      <div style={{ padding: '30px' }}>
        <LoadScript googleMapsApiKey="AIzaSyA94J1dEXShLTlOZmIZbmOT31Qyo2g-2OU">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={irelandCenter}
            zoom={mapZoom}
            onClick={() => {
              setSelectedDriver(null);
              setSelectedPin(null);
              setHoverDestination(null);
            }}
            options={{
              mapTypeId: 'hybrid',
              streetViewControl: false,
              mapTypeControl: true,
              rotateControl: false,
            }}>
            {/* Drivers */}
            {(selectedData === 'Show All Data' || selectedData === 'Show drivers only') &&
              drivers
                ?.filter(d => isValidLatLng(d.latitude, d.longitude))
                .map(driver => (
                  <Marker
                    key={driver.id}
                    position={{ lat: driver.latitude, lng: driver.longitude }}
                    onClick={() => setSelectedDriver(driver)}
                    title={driver.username}
                    icon={{
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(driverIconSvg)}`,
                      scaledSize: { width: 40, height: 40 },
                      anchor: { x: 20, y: 20 },
                    }}
                  />
                ))}

            {/* Pins */}
            {(selectedData === 'Show All Data' || selectedData === 'Show active sites only') &&
              uniquePins
                ?.filter(p => isValidLatLng(p.latitude, p.longitude))
                .map(pin => (
                  <Marker
                    key={`${pin.siteAssessmentID}-${pin.id}`}
                    position={{ lat: pin.latitude, lng: pin.longitude }}
                    onClick={() => setSelectedPin(pin)}
                    title={`${pin.mapLabel} (Site: ${pin.siteAssessmentID})`}
                    icon={{
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pinIconSvg)}`,
                      scaledSize: new window.google.maps.Size(40, 40),
                      anchor: new window.google.maps.Point(20, 20),
                    }}
                  />
                ))}

            {/* Destinations */}
            {(selectedData === 'Show All Data' || selectedData === 'Show destinations only') &&
              allDestinations
                ?.filter(dest => isValidLatLng(dest.latitude, dest.longitude))
                .map(destination => (
                  <Marker
                    key={destination.id}
                    position={{ lat: destination.latitude, lng: destination.longitude }}
                    onClick={() => setHoverDestination(destination)}
                    title={destination.destinationName}
                    icon={{
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(destinationIconSvg)}`,
                      scaledSize: new window.google.maps.Size(40, 40),
                      anchor: new window.google.maps.Point(20, 20),
                    }}
                  />
                ))}

            {/* Destination Info */}
            {hoverDestination && (
              <InfoWindow
                position={{ lat: hoverDestination.latitude, lng: hoverDestination.longitude }}
                onCloseClick={() => setHoverDestination(null)}>
                <div style={{ fontWeight: 'bold' }}>{hoverDestination.destinationName}</div>
              </InfoWindow>
            )}

            {/* Polygons */}
            {polyLine?.map(sitePoly => {
              const grouped = sitePoly.data.reduce((groups, p) => {
                if (!groups[p.mapLabel]) groups[p.mapLabel] = { path: [], info: p };
                groups[p.mapLabel].path.push({
                  lat: p.latitude,
                  lng: p.longitude,
                  sortOrder: p.sortOrder,
                });
                return groups;
              }, {});

              Object.values(grouped).forEach(g => {
                g.path.sort((a, b) => a.sortOrder - b.sortOrder);
              });

              return Object.values(grouped)
                .filter(({ info }) => info.mapLabel?.toLowerCase().includes('boundry'))
                .map(({ path, info }, idx) => (
                  <Polygon
                    key={`${sitePoly.siteAssessmentID}-boundry-${idx}`}
                    paths={path}
                    options={{
                      fillColor: '#ffffff',
                      fillOpacity: 0.05,
                      strokeColor: '#000000',
                      strokeOpacity: 0.8,
                      strokeWeight: 3,
                    }}
                    onClick={e =>
                      setSelectedArea({
                        position: e.latLng,
                        name: `${info.displayName} (Site: ${sitePoly.siteAssessmentID})`,
                      })
                    }
                  />
                ));
            })}

            {/* Area Info */}
            {selectedArea && (
              <InfoWindow position={selectedArea.position} onCloseClick={() => setSelectedArea(null)}>
                <div style={{ fontWeight: 'bold' }}>{selectedArea.name}</div>
              </InfoWindow>
            )}

            {/* Driver Info */}
            {selectedDriver && (
              <InfoWindow
                position={{ lat: selectedDriver.latitude, lng: selectedDriver.longitude }}
                onCloseClick={() => setSelectedDriver(null)}>
                <div>
                  <h4>🚛 Driver Details</h4>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Username:</strong>
                    <span style={{ marginLeft: '8px' }}>{selectedDriver.username}</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Location:</strong>
                    <span style={{ marginLeft: '8px' }}>
                      {selectedDriver.latitude.toFixed(4)}, {selectedDriver.longitude.toFixed(4)}
                    </span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Last Update:</strong>
                    <span style={{ marginLeft: '8px' }}>
                      {new Date(selectedDriver.recordDate).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </span>
                  </div>
                  <div>Driver ID: {selectedDriver.userID}</div>
                </div>
              </InfoWindow>
            )}

            {/* Pin Info */}
            {selectedPin && (
              <InfoWindow
                position={{ lat: selectedPin.latitude, lng: selectedPin.longitude }}
                onCloseClick={() => setSelectedPin(null)}>
                <div>
                  <h5>Site Id - {selectedPin.siteAssessmentID}</h5>
                  <h5>{selectedPin.mapLabel}</h5>
                  <div>
                    <strong>Location:</strong>
                    <span style={{ marginLeft: '8px' }}>
                      {selectedPin.latitude.toFixed(4)}, {selectedPin.longitude.toFixed(4)}
                    </span>
                  </div>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </div>
    </div>
  );
};

export default MapView;
